import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  AppBar,
  Toolbar,
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DocumentationViewerSimple: React.FC = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDocumentation();
  }, []);

  const fetchDocumentation = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/marketing/docs/get-started/');

      console.log('Documentation response status:', response.status);
      console.log('Documentation response headers:', response.headers.get('content-type'));

      if (!response.ok) {
        throw new Error(`Failed to load documentation: ${response.status} ${response.statusText}`);
      }

      const text = await response.text();
      console.log('Documentation content length:', text.length);
      console.log('First 200 chars of content:', text.substring(0, 200));
      console.log('Content starts with HTML?:', text.trim().startsWith('<'));

      setContent(text);
    } catch (err) {
      console.error('Error fetching documentation:', err);
      setError(err instanceof Error ? err.message : 'Failed to load documentation');
    } finally {
      setLoading(false);
    }
  };

  const downloadMarkdown = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'marketing_get_started_zh.md';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate('/marketing')} sx={{ mr: 2 }}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            营销功能入门指南
          </Typography>
          <IconButton onClick={downloadMarkdown} color="primary">
            <DownloadIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3, backgroundColor: '#f5f5f5' }}>
        <Paper sx={{ maxWidth: 1200, mx: 'auto', p: 4 }}>
          {!content ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" gutterBottom>
                无法加载文档
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                请确保后端服务正在运行，并重启前端开发服务器。
              </Typography>
              <Typography variant="body2" color="textSecondary">
                运行: <code>cd frontend && npm run dev</code>
              </Typography>
            </Box>
          ) : content.trim().startsWith('<') ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                检测到HTML内容而非Markdown文档。请重启前端服务以应用代理配置。
              </Alert>
              <Box
                sx={{
                  '& pre': {
                    backgroundColor: '#f8f8f8',
                    padding: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                    maxHeight: '600px',
                  },
                }}
              >
                <pre>{content.substring(0, 2000)}...</pre>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                '& pre': {
                  backgroundColor: '#f8f8f8',
                  padding: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif',
                  fontSize: '0.95rem',
                  lineHeight: 1.8,
                  color: '#333',
                },
              }}
            >
              <pre>{content}</pre>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default DocumentationViewerSimple;